import moment from "moment";

import type { ExternalRegisterPatientSummary } from "@/components/externalregister/types";
import type { Patient } from "@/resources/types";
import type { PatientStatusEncounterSummary } from "./patientStatusWidgetHelpers";

const linkedPatientRestoreFields = [
  "submissionType",
  "firstName",
  "lastName",
  "phone",
  "email",
  "dob",
  "sex",
  "gender",
  "pronouns",
  "medicareNo",
  "medicareNoPos",
  "umrn",
  "allergies",
  "noAllergies",
  "gpClinicName",
  "gpDoctorFirstName",
  "gpDoctorLastName",
  "nextOfKinFirstName",
  "nextOfKinLastName",
  "nextOfKinRelationship",
  "previousNames",
  "anonymous",
  "unknown",
] as const;

const emptyExternalRegisterSummary = {
  hasExternalRegisterAlert: false,
  externalRegisterAlerts: [],
};

const emptyPatientFieldValues = {
  submissionType: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  dob: undefined,
  sex: "",
  gender: "",
  pronouns: "",
  medicareNo: "",
  medicareNoPos: "",
  umrn: "",
  allergies: [],
  noAllergies: false,
  gpClinicName: "",
  gpDoctorFirstName: "",
  gpDoctorLastName: "",
  nextOfKinFirstName: "",
  nextOfKinLastName: "",
  nextOfKinRelationship: "",
  previousNames: [],
  anonymous: false,
  unknown: false,
};

export const getCurrentPatientFormSnapshot = (getValues: (name: string) => any) => {
  const snapshot: Record<string, any> = {};
  linkedPatientRestoreFields.forEach((field) => {
    snapshot[field] = getValues(field);
  });
  return snapshot;
};

export const ensureMomentDate = (value: unknown): moment.Moment | undefined => {
  if (!value) return undefined;
  if (moment.isMoment(value)) return value.isValid() ? value : undefined;
  if (typeof value === "string" || typeof value === "number") {
    const m = moment(value, "YYYY-MM-DD");
    return m.isValid() ? m : undefined;
  }
  return undefined;
};

export const patientToExternalRegisterSummary = (patient: Patient): ExternalRegisterPatientSummary => ({
  hasExternalRegisterAlert: (patient as Partial<ExternalRegisterPatientSummary>).hasExternalRegisterAlert === true,
  externalRegisterAlerts: (patient as Partial<ExternalRegisterPatientSummary>).externalRegisterAlerts || [],
});

// eslint-disable-next-line complexity
export const patientToGenericFormValues = (patient: Patient) => {
  let submissionType = "";
  if (patient.firstName === "****" && patient.lastName === "****") {
    submissionType = "anonymous";
  } else if (patient.unknown) {
    submissionType = "unknown";
  }

  const recentEncounters = (patient as { recentEncounters?: PatientStatusEncounterSummary[] }).recentEncounters;

  return {
    submissionType,
    firstName: patient.firstName || "",
    lastName: patient.lastName || "",
    phone: patient.phone || "",
    email: patient.email || "",
    dob: ensureMomentDate(patient.dateOfBirth),
    sex: patient.sex || "",
    gender: patient.gender || "",
    pronouns: patient.pronouns || "",
    medicareNo: patient.medicareNo || "",
    medicareNoPos: patient.medicareNoPos || "",
    umrn: patient.umrn || "",
    allergies: patient.allergies || [],
    noAllergies: (patient as any).noAllergies ?? ((patient.allergies?.length ?? 0) === 0),
    gpClinicName: patient.gpDetails?.clinicName || "",
    gpDoctorFirstName: patient.gpDetails?.doctorFirstName || "",
    gpDoctorLastName: patient.gpDetails?.doctorLastName || "",
    nextOfKinFirstName: patient.nextOfKin?.firstName || "",
    nextOfKinLastName: patient.nextOfKin?.lastName || "",
    nextOfKinRelationship: patient.nextOfKin?.relationship || "",
    previousNames: patient.previousNames || [],
    _externalRegisterSummary: patientToExternalRegisterSummary(patient),
    _patientStatusRecentEncounters: Array.isArray(recentEncounters) ? recentEncounters : undefined,
  };
};

export const applyExistingPatientToGenericForm = async ({
  patient,
  getValues,
  setValue,
  trigger,
}: {
  patient: Patient;
  getValues?: (name: string) => any;
  setValue: (name: string, value: any, options?: any) => void;
  trigger?: () => Promise<boolean>;
}) => {
  if (getValues && !getValues("_existingPatientId") && !getValues("_preExistingPatientSnapshot")) {
    setValue("_preExistingPatientSnapshot", getCurrentPatientFormSnapshot(getValues));
  }

  const values = patientToGenericFormValues(patient);
  Object.entries(values).forEach(([key, value]) => {
    setValue(key, value);
  });

  if (patient.id) {
    setValue("_existingPatientId", patient.id);
  }

  setValue("_encounterId", undefined);
  setValue("_isEditMode", false);
  setValue("_encounterOriginalStatus", "");
  setValue("_resumeExpectedEncounter", false);

  await trigger?.();
};

export const clearExistingPatientFromGenericForm = async ({
  getValues,
  setValue,
  trigger,
}: {
  getValues: (name: string) => any;
  setValue: (name: string, value: any, options?: any) => void;
  trigger?: () => Promise<boolean>;
}) => {
  const snapshot = getValues("_preExistingPatientSnapshot") || emptyPatientFieldValues;

  Object.entries(snapshot).forEach(([key, value]) => {
    setValue(key, value);
  });

  setValue("_existingPatientId", undefined);
  setValue("_externalRegisterSummary", emptyExternalRegisterSummary);
  setValue("_patientStatusRecentEncounters", undefined);
  setValue("_preExistingPatientSnapshot", undefined);

  await trigger?.();
};
